import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useLocationCoverage } from '../../hooks/useTrainingLocations'
import ProposeExerciseButton from './ProposeExerciseButton'
import AssignExerciseButton from './AssignExerciseButton'
import { locationKeys } from '../../hooks/useTrainingLocations'
import ExerciseFormModal from '../exercises/ExerciseFormModal'

/**
 * VH-126 Stage C: what can actually be trained here, and what is going to waste.
 *
 * A scanned location used to yield inventory and nothing else — twenty machines
 * on screen and no way to tell which exercises they made possible. Two numbers
 * answer that, and they only mean something together: "24 possible" is empty
 * praise beside "7 machines you cannot use".
 *
 * The uncovered list is the important half. It is the gap, and it becomes the
 * entry point to Stage D's AI-proposed exercises.
 */
export default function CoveragePanel({ locationId }) {
  const { t } = useTranslation()
  const { data, isLoading, error } = useLocationCoverage(locationId)
  const [showPossible, setShowPossible] = useState(false)
  const [showNearMisses, setShowNearMisses] = useState(false)
  // VH-126 Stage D: the draft awaiting review. Held here rather than inside the
  // button so the review panel can sit beside the gap list it came from.
  const [proposal, setProposal] = useState(null)
  const queryClient = useQueryClient()

  /**
   * The AI draft opens in the Exercise Library's own form (VH-268) — the one
   * manual authoring uses — so type, sets and metrics are all editable before
   * anything is saved. VH-126 asks for one form, not two.
   *
   * The model's set counts are dropped: the form starts from the 5 working /
   * 0 warm-up default, visible and editable (reported from the gym 2026-09-16:
   * an unseen 3 / 1 beat the default). No video: Equipment owns it (VH-125 C).
   */
  const proposedExercise = (p) => {
    const draft = { ...(p.proposal || {}), equipment_id: p.equipment_id }
    delete draft.default_training_sets
    delete draft.default_warmup_sets
    return draft
  }

  const closeProposal = () => setProposal(null)

  const proposalSaved = () => {
    // The machine leaves the gap list the moment this lands, so both counts
    // and the uncovered list have to be refetched.
    queryClient.invalidateQueries({ queryKey: ['training-locations'] })
    queryClient.invalidateQueries({ queryKey: ['exercises'] })
    setProposal(null)
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-2" data-testid="coverage-loading">
        <div className="h-6 w-2/3 rounded bg-gray-200" />
        <div className="h-20 rounded bg-gray-200" />
      </div>
    )
  }

  if (error) {
    return (
      <p className="text-sm text-red-600" role="alert" data-testid="coverage-error">
        {t('trainingLocations.coverage.failed')}
      </p>
    )
  }

  if (!data) return null

  const { possible_count: possible, uncovered_count: uncovered } = data
  const nearMisses = data.near_misses || []

  return (
    <section className="mt-8" data-testid="coverage-panel">
      <h2 className="mb-3 text-xl font-semibold text-gray-900">
        {t('trainingLocations.coverage.title')}
      </h2>

      {/* The two numbers, side by side, because neither is legible alone. */}
      <div className="mb-4 grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-gray-200 bg-surface-container p-4">
          <p className="text-2xl font-semibold text-gray-900" data-testid="possible-count">
            {possible}
          </p>
          <p className="text-sm text-gray-600">
            {t('trainingLocations.coverage.possible')}
          </p>
        </div>
        <div
          className={`rounded-lg border p-4 ${
            uncovered > 0
              ? 'border-amber-300 bg-amber-50'
              : 'border-gray-200 bg-surface-container'
          }`}
        >
          <p className="text-2xl font-semibold text-gray-900" data-testid="uncovered-count">
            {uncovered}
          </p>
          <p className="text-sm text-gray-600">
            {t('trainingLocations.coverage.uncovered')}
          </p>
        </div>
      </div>

      {/* The gap list, expanded by default when there IS a gap — it is the
          thing worth acting on, and collapsing it would bury the point. */}
      {uncovered > 0 && (
        <div className="mb-4" data-testid="uncovered-list">
          <h3 className="mb-2 text-sm font-medium text-gray-700">
            {t('trainingLocations.coverage.uncoveredHeading')}
          </h3>
          <ul className="grid gap-2 sm:grid-cols-2">
            {data.uncovered_equipment.map((item) => (
              <li
                key={item.id}
                className="flex items-center gap-3 rounded-lg border border-gray-200 bg-surface-container p-3"
                data-testid={`uncovered-${item.id}`}
              >
                {item.photo_url ? (
                  <img
                    src={item.photo_url}
                    alt={item.name_en}
                    className="h-12 w-12 rounded object-cover"
                  />
                ) : (
                  <div className="h-12 w-12 rounded bg-gray-100" aria-hidden="true" />
                )}
                <div className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-gray-900">
                    {item.name_en}
                  </span>
                  {/* VH-126 Stage D: the gap list is where a proposal is
                      worth asking for, so the action lives on the gap. */}
                  {/* Three ways to cover a machine, in the order they cost
                      the user: reuse something you already wrote, ask the AI,
                      or author one by hand (VH-134 added the first). */}
                  <div className="mt-1 flex flex-wrap gap-2">
                    <AssignExerciseButton
                      equipment={item}
                      onAssigned={() =>
                        queryClient.invalidateQueries({
                          queryKey: locationKeys.detail(locationId),
                        })
                      }
                    />
                    <ProposeExerciseButton
                      equipment={item}
                      onProposal={setProposal}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {proposal && (
        <ExerciseFormModal
          exercise={proposedExercise(proposal)}
          equipment={[{ id: proposal.equipment_id, name_en: proposal.equipment_name }]}
          getEquipmentName={(eq) => eq?.name_en || ''}
          getEquipmentLabel={(eq) => eq?.name_en || ''}
          notices={[proposal.muscle_mismatch_warning, proposal.duplicate_warning]}
          extraPayload={{ is_ai_assisted: true }}
          onClose={closeProposal}
          onSuccess={proposalSaved}
        />
      )}

      {uncovered === 0 && (
        <p className="mb-4 text-sm text-gray-600" data-testid="nothing-uncovered">
          {t('trainingLocations.coverage.allCovered')}
        </p>
      )}

      {/* Near misses: named rather than dropped. An exercise silently missing
          from the catalogue reads as a bug; one listed as "needs a rack" reads
          as a shopping list. */}
      {nearMisses.length > 0 && (
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setShowNearMisses((v) => !v)}
            className="flex items-center text-sm text-gray-700 hover:text-gray-900"
            data-testid="near-misses-toggle"
          >
            {showNearMisses ? (
              <ChevronDownIcon className="mr-1 h-4 w-4" />
            ) : (
              <ChevronRightIcon className="mr-1 h-4 w-4" />
            )}
            {t('trainingLocations.coverage.nearMisses', { count: nearMisses.length })}
          </button>
          {showNearMisses && (
            <ul className="mt-2 space-y-1" data-testid="near-miss-list">
              {nearMisses.map((miss) => (
                <li
                  key={miss.exercise_id}
                  className="text-sm text-gray-600"
                  data-testid={`near-miss-${miss.exercise_id}`}
                >
                  {miss.name_en}
                  {' — '}
                  {t('trainingLocations.coverage.missing', {
                    items: miss.missing_equipment_names.join(', '),
                  })}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* VH-134: the handoff from "here is what this gym can do" to "build me
          a session from it". Without it the flow stopped at a covered location
          and nothing carried the user into planning. Hidden when there is
          nothing to build from — an empty picker is worse than an explanation. */}
      {data.possible_count > 0 ? (
        <Link
          to={`/templates?location=${locationId}`}
          className="btn-primary btn-sm mb-3 inline-flex items-center"
          data-testid="build-module-here"
        >
          {t('trainingLocations.module.buildHere')}
        </Link>
      ) : (
        <p className="mb-3 text-sm text-gray-500" data-testid="nothing-to-build">
          {t('trainingLocations.module.nothingToBuildFrom')}
        </p>
      )}

      <button
        type="button"
        onClick={() => setShowPossible((v) => !v)}
        className="flex items-center text-sm text-gray-700 hover:text-gray-900"
        data-testid="possible-toggle"
      >
        {showPossible ? (
          <ChevronDownIcon className="mr-1 h-4 w-4" />
        ) : (
          <ChevronRightIcon className="mr-1 h-4 w-4" />
        )}
        {t('trainingLocations.coverage.showPossible')}
      </button>
      {showPossible && (
        <ul className="mt-2 grid gap-1 sm:grid-cols-2" data-testid="possible-list">
          {data.possible_exercises.map((ex) => (
            <li
              key={ex.id}
              className="text-sm text-gray-700"
              data-testid={`possible-${ex.id}`}
            >
              {ex.name_en}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
